// src/components/Ticker.jsx
// Horizontally scrolling strip of recent results + upcoming fixtures.
// Reads from /api/feed — filters articles with type "result" or "preview"
// published in the last 7 days. No extra API calls. Updates 2x daily with runs.

import { useState, useEffect } from "react";
import { parseArticles, normalize } from "../utils/sheet";
import { apiFetch } from "../utils/api";

const SPORT_ICONS = {
  football:  "\u26BD",
  cricket:   "\uD83C\uDFCF",
  tennis:    "\uD83C\uDFBE",
  badminton: "\uD83C\uDFF8",
  multi:     "\uD83C\uDFC6",
};

function isResult(a) {
  const ctx = normalize(a["context"] || "");
  const title = normalize(a["title"] || "");
  return ctx.includes("result") || title.includes(" win ") || title.includes(" beat ") || title.includes(" defeat ");
}

function isPreview(a) {
  const ctx = normalize(a["context"] || "");
  const title = normalize(a["title"] || "");
  return ctx.includes("preview") || ctx.includes("fixture") || title.includes(" vs ");
}

function getSport(a) {
  return ((a["sport"] || a["category"] || "football")).toLowerCase().trim();
}

function getDate(a) {
  const ts = parseInt(a["date"] || a["Date"] || 0, 10);
  return ts ? new Date(ts * 1000) : null;
}

function formatTickerItem(a) {
  const sport   = getSport(a);
  const icon    = SPORT_ICONS[sport] || "\uD83C\uDFC6";
  const title   = (a["title"] || "").slice(0, 80);
  const isRes   = isResult(a);
  const badge   = isRes ? "FT" : "UPCOMING";
  const bcolor  = isRes ? "#4ade80" : "#facc15";

  return { icon, title, badge, bcolor };
}

// CSS keyframes injected once
const STYLE = `
@keyframes gt-ticker-scroll {
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}
.gt-ticker-track {
  display: flex;
  align-items: center;
  animation: gt-ticker-scroll linear infinite;
  animation-duration: var(--ticker-speed, 40s);
  white-space: nowrap;
}
.gt-ticker-track:hover {
  animation-play-state: paused;
}
`;

export default function Ticker() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    // Inject keyframe CSS once
    if (!document.getElementById("gt-ticker-css")) {
      const s = document.createElement("style");
      s.id = "gt-ticker-css";
      s.textContent = STYLE;
      document.head.appendChild(s);
    }

    apiFetch("/api/feed")
      .then(r => r.text())
      .then(raw => {
        const now     = Date.now();
        const week    = 7 * 24 * 60 * 60 * 1000;
        const parsed  = parseArticles(raw);

        const tickerItems = parsed
          .filter(a => {
            const d = getDate(a);
            if (!d) return false;
            const age = now - d.getTime();
            return age >= 0 && age <= week;
          })
          .filter(a => isResult(a) || isPreview(a))
          .sort((a, b) => {
            const da = getDate(a) || 0;
            const db = getDate(b) || 0;
            return db - da;
          })
          .slice(0, 20)
          .map(formatTickerItem);

        setItems(tickerItems);
      })
      .catch(() => {});
  }, []);

  if (items.length === 0) return null;

  // Duplicate items for seamless loop
  const doubled = [...items, ...items];
  // Speed: roughly 60px per item per second — longer at more items
  const speed   = Math.max(25, items.length * 4);

  return (
    <div
      style={{
        height: "34px",
        background: "#0a0a0a",
        borderBottom: "1px solid #1a1a1a",
        overflow: "hidden",
        position: "fixed",
        top: "56px",   /* sits directly under the 14-unit (56px) header */
        left: 0,
        right: 0,
        zIndex: 40,
        display: "flex",
        alignItems: "center",
      }}
    >
      {/* LEFT LABEL */}
      <div style={{
        flexShrink: 0,
        padding: "0 14px",
        borderRight: "1px solid #1f1f1f",
        height: "100%",
        display: "flex",
        alignItems: "center",
        background: "#0a0a0a",
        zIndex: 2,
      }}>
        <span style={{
          color: "#a3e635",
          fontSize: "9px",
          fontFamily: "monospace",
          fontWeight: 700,
          letterSpacing: "0.2em",
          textTransform: "uppercase",
        }}>
          Live
        </span>
      </div>

      {/* SCROLLING TRACK */}
      <div style={{ overflow: "hidden", flex: 1, height: "100%", display: "flex", alignItems: "center" }}>
        <div
          className="gt-ticker-track"
          style={{ "--ticker-speed": `${speed}s` }}
        >
          {doubled.map((item, i) => (
            <span key={i} style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "0 24px 0 0",
              color: "#71717a",
              fontSize: "11px",
              fontFamily: "monospace",
            }}>
              {/* Separator dot */}
              <span style={{ color: "#27272a", marginRight: "8px" }}>◆</span>
              {/* Sport icon */}
              <span style={{ fontSize: "12px" }}>{item.icon}</span>
              {/* Badge */}
              <span style={{
                fontSize: "8px",
                fontWeight: 700,
                letterSpacing: "0.1em",
                padding: "1px 5px",
                border: `1px solid ${item.bcolor}`,
                color: item.bcolor,
                borderRadius: "2px",
              }}>
                {item.badge}
              </span>
              {/* Title */}
              <span style={{ color: "#a1a1aa" }}>{item.title}</span>
            </span>
          ))}
        </div>
      </div>

      {/* RIGHT FADE */}
      <div style={{
        position: "absolute",
        right: 0,
        top: 0,
        bottom: 0,
        width: "60px",
        background: "linear-gradient(to left, #0a0a0a, transparent)",
        pointerEvents: "none",
      }} />
    </div>
  );
}
